import axios from 'axios';
import { AppConfig, getApiUrl } from 'config/appConfig';
import { ResolvedTenant } from 'types/tenant';
import {
  findAdminTokenForTenant,
  migrateLegacyStoreToken,
  readTenantFromAdminToken,
  readTenantFromTokenClaims,
} from 'utils/jwtClaims';
import { ENDPOINTS } from 'api/endpoints';
import { ROUTES } from 'config/routes';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

const isLocalHost = (hostname: string): boolean => LOCAL_HOSTS.has(hostname.toLowerCase());

const parseSubdomainTenantCode = (hostname: string, baseDomain?: string): string | null => {
  if (!baseDomain) return null;
  const normalizedBase = baseDomain.toLowerCase();
  const normalizedHost = hostname.toLowerCase();
  const suffix = `.${normalizedBase}`;
  if (!normalizedHost.endsWith(suffix)) return null;
  const prefix = normalizedHost.slice(0, -suffix.length);
  const code = prefix.split('.').filter(Boolean)[0];
  return code || null;
};

const resolveFromApi = async (
  hostname: string,
  baseDomain?: string
): Promise<ResolvedTenant | null> => {
  try {
    const response = await axios.get<ResolvedTenant>(`${getApiUrl()}${ENDPOINTS.TENANT_RESOLVE}`, {
      params: { host: hostname, baseDomain },
    });
    return response.data;
  } catch {
    return null;
  }
};

const resolveFromConfigFallback = (config: AppConfig, hostname: string): ResolvedTenant | null => {
  if (!isLocalHost(hostname) || !config.tenantCode) return null;
  return {
    tenantId: 0,
    tenantCode: config.tenantCode,
    siteName: config.siteName,
  };
};

const readHandoffTokenFromUrl = (): string | null => {
  if (!window.location.pathname.startsWith(ROUTES.ADMIN_AUTH_CALLBACK)) return null;
  const token = new URLSearchParams(window.location.search).get('token');
  return token?.trim() || null;
};

const toResolvedTenant = (
  claims: { tenantId: number; tenantCode: string },
  config: AppConfig,
  siteName?: string
): ResolvedTenant => ({
  tenantId: claims.tenantId,
  tenantCode: claims.tenantCode,
  siteName: siteName || config.siteName,
});

const resolveFromHostname = async (config: AppConfig): Promise<ResolvedTenant | null> => {
  const hostname = window.location.hostname;

  const fromApi = await resolveFromApi(hostname, config.tenantBaseDomain);
  if (fromApi) return fromApi;

  const subdomainCode = parseSubdomainTenantCode(hostname, config.tenantBaseDomain);
  if (subdomainCode) {
    return {
      tenantId: 0,
      tenantCode: subdomainCode,
      siteName: config.siteName,
    };
  }

  return resolveFromConfigFallback(config, hostname);
};

const tenantResolverService = {
  resolve: async (config: AppConfig): Promise<ResolvedTenant | null> => {
    // 1) Handoff ERP: el JWT admin es autoritativo.
    const handoffToken = readHandoffTokenFromUrl();
    if (handoffToken) {
      const fromHandoff = readTenantFromAdminToken(handoffToken);
      if (fromHandoff) {
        return toResolvedTenant(fromHandoff, config);
      }
    }

    // 2) Hostname / API / subdominio / localhost.
    const fromHost = await resolveFromHostname(config);
    if (!fromHost) return null;

    migrateLegacyStoreToken(fromHost.tenantCode);

    // 3) Si hay JWT admin del tenant del host, alinear claims (tenantId real).
    const adminToken = findAdminTokenForTenant(fromHost.tenantCode);
    if (adminToken) {
      const fromAdmin = readTenantFromTokenClaims(adminToken);
      if (
        fromAdmin &&
        fromAdmin.tenantCode.toLowerCase() === fromHost.tenantCode.toLowerCase()
      ) {
        return toResolvedTenant(fromAdmin, config, fromHost.siteName);
      }
    }

    return fromHost;
  },
};

export default tenantResolverService;
